import type { ModelMeta, ModelTargetView } from "@/types";

// #P001 (UC-2): wie ein Modell-Eintrag in einer Auswahlliste heisst. Formel-Zwilling von `/model`
// im Chat (`management-service/.../SlashCommandService.ts`) — der Nutzer soll überall dasselbe
// lesen; bei Änderung beide Seiten (getrennte Pakete, kein gemeinsames lib). Nie Basis-URL oder
// Schlüssel: nur Modell und Anbieter.
//
// Verzweigt über die ART (`kind`), nicht über die Existenz von `target` (Review MED-2): ein Slot
// ohne Ziel-Angabe (Fix auf einem Alt-Eintrag, gelöschtes Routing) ist KEIN Anthropic-Direktweg —
// genau so ein Name wird vom Master mit `MODEL_UNRESOLVABLE` abgewiesen, und das muss man lesen
// können. Ein inaktiver Eintrag (kommt nur als `fixedTarget` vor) trägt „(nicht aktiv)" (MED-3).
// #P002 (UC-1): das Metadaten-Kürzel. Nur die STRENGE Richtung wird benannt — „Cloud" und
// „nicht für heikle Daten" sind der Normalfall und blähten jede Zeile auf. Fehlt `meta` (älterer
// Master), steht dort NICHTS: ein erfundenes „Cloud" wäre eine Aussage, die niemand gemacht hat.
export function metaKuerzel(meta: ModelMeta | undefined): string {
  if (!meta) return "";
  return `${meta.hosting === "local" ? " · lokal" : ""}${meta.sensitiveDataOk ? " · heikel ok" : ""}`;
}

export function modelOptionLabel(view: ModelTargetView): string {
  const inaktiv = view.enabled ? "" : " (nicht aktiv)";
  // Reihenfolge: erst WAS es ist, dann was es DARF, zuletzt ob es überhaupt greift.
  const kuerzel = metaKuerzel(view.meta);
  if (view.kind === "anthropic") return `${view.name} · Anthropic direkt${kuerzel}${inaktiv}`;
  return view.target
    ? `${view.name} — ${view.target.model} @ ${view.target.provider}${kuerzel}${inaktiv}`
    : `${view.name} — Ziel unbekannt${kuerzel}${inaktiv}`;
}

import type { AgentModelsView } from "@/types";

/** Ein Eintrag einer Modell-Auswahlliste: der Wert am Wire, die Beschriftung für Menschen. */
export interface ModelOption {
  value: string;
  label: string;
}

// #P001 (UC-4, DRY-Nachzug LOW-8 aus UC-3): ein GESPEICHERTER Wert, den die Liste nicht (mehr)
// enthält, darf nicht still aus der Maske verschwinden — sonst sähe ein gesetztes Modell aus
// wie „nicht gesetzt", und ein Speichern schriebe es weg, ohne dass es jemand wollte. Er bleibt
// als letzte Option stehen.
//
// `belastbar` = ist die Auskunft „steht nicht mehr zur Wahl" überhaupt gedeckt? Nur dann trägt
// die Option den Marker; solange die Liste lädt oder ein älterer Master sie gar nicht liefert,
// steht dort der nackte Name (ein erfundenes Urteil wäre schlimmer als keines, Review LOW-3).
//
// Pur und ohne React — dieselbe Regel an allen drei Stellen (Einrichtung, Agent-Defaults,
// Start-Dialog), statt dreimal derselben vier Zeilen.
export function mitAltwert(
  optionen: readonly ModelOption[],
  wert: string | null | undefined,
  belastbar: boolean,
): ModelOption[] {
  if (!wert || optionen.some((o) => o.value === wert)) return [...optionen];
  return [...optionen, { value: wert, label: belastbar ? `${wert} (nicht mehr wählbar)` : wert }];
}

// #P001 (UC-5, Review MED-3): DIE Ableitung „Antwort von GET /agents/:id/models → Optionen" — an
// drei Pickern (Einrichtung, Start-Dialog, Lauf-Detail) stand sie je einmal und begann zu driften.
// `targets` beschreibt GENAU die wählbaren Namen; fehlt es (älterer Master), bleibt die blosse
// Namensliste — lieber kein Ziel als ein erfundenes. `selectable` ist Vertrag (Pflicht).
export function modelBasisAus(d: AgentModelsView | undefined): ModelOption[] {
  if (!d) return [];
  return d.targets
    ? d.targets.map((t) => ({ value: t.name, label: modelOptionLabel(t) }))
    : d.selectable.map((name) => ({ value: name, label: name }));
}

// Der Admin-Fix als Beschriftung — sein ZIEL, nicht nur der Name; `null` = kein Fix.
export function fixLabelAus(d: AgentModelsView | undefined): string | null {
  if (!d?.fixedModel) return null;
  return d.fixedTarget ? modelOptionLabel(d.fixedTarget) : d.fixedModel;
}

/**
 * #P002 (UC-5): die Optionen eines LAUF-Modell-Pickers — gefiltert, wenn der Master es sagt.
 *
 * `choices` sind die Ziel-Anzeigen, die der Master bereits auf „erfüllt die Anforderungen
 * ALLER Schritte, die auf dem Lauf-Modell laufen würden" eingeschränkt hat
 * (`runModelChoices` an der Einrichtung, `modelChoices` am Lauf). Fehlt das Feld
 * (`undefined` = älterer Master) oder ist es `null` (= nicht ermittelbar), bleibt es beim
 * bisherigen Verhalten: die ungefilterte Basis. **Nie eine leere Liste erfinden** — eine
 * leere Auswahl ist eine Aussage, die wir in diesen beiden Fällen nicht machen können.
 *
 * `gefiltert` sagt der Oberfläche, ob sie den Hinweis „nur Modelle, die alle Schritte
 * erfüllen" zeigen soll — nämlich genau dann, wenn dadurch wirklich etwas wegfällt.
 */
export function laufModellOptionen(
  choices: readonly ModelTargetView[] | null | undefined,
  basis: readonly ModelOption[],
): { optionen: ModelOption[]; gefiltert: boolean } {
  if (!choices) return { optionen: [...basis], gefiltert: false };
  const optionen = choices.map((t) => ({ value: t.name, label: modelOptionLabel(t) }));
  return { optionen, gefiltert: optionen.length < basis.length };
}
